import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { reservationAPI, logsAPI } from '../api/client';
import Layout from '../components/Layout';

const STATUS_BADGE = {
  success: 'bg-emerald-50 text-mint rounded-full px-2.5 py-0.5',
  failed: 'bg-coral/10 text-coral rounded-full px-2.5 py-0.5',
};
const STATUS_TEXT = { success: '성공', failed: '실패' };

export default function Reservations() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const showToast = useToastStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const userId = user?.userId;

  const [tab, setTab] = useState('reservations'); // 'reservations' | 'logs'
  const [cancellingReservation, setCancellingReservation] = useState(null);
  const [logOffset, setLogOffset] = useState(0);
  const logLimit = 50;

  if (!isAuthenticated) { navigate('/login'); return null; }

  // ─── 현재 예약 ───
  const { data: reservationsData, isLoading: loadingReservations, error: reservationsError } = useQuery({
    queryKey: ['reservations', userId],
    queryFn: async () => {
      const response = await reservationAPI.getReservations(userId);
      if (response.success) return response.data.reservations || [];
      throw new Error(response.error || '예약 내역을 불러오는데 실패했습니다.');
    },
    enabled: !!userId && tab === 'reservations',
  });

  const reservations = reservationsData || [];

  const cancelMutation = useMutation({
    mutationFn: (reservation) => reservationAPI.cancel({ userId, reservation }),
    onMutate: (reservation) => setCancellingReservation(`${reservation.busSeq}-${reservation.seatNo}`),
    onSuccess: (response) => {
      if (response.success) {
        showToast('예약이 취소되었습니다.', 'success');
        queryClient.invalidateQueries({ queryKey: ['reservations', userId] });
      } else {
        showToast(response.error || '예약 취소에 실패했습니다.', 'error');
      }
    },
    onError: (err) => showToast(err.response?.data?.error || '예약 취소에 실패했습니다.', 'error'),
    onSettled: () => setCancellingReservation(null),
  });

  const handleCancel = (reservation) => {
    if (!confirm(`${reservation.routeName} ${reservation.seatNo}번 좌석 예약을 취소하시겠습니까?`)) return;
    cancelMutation.mutate(reservation);
  };

  // ─── 예약 로그 ───
  const { data: logsData, isLoading: loadingLogs, error: logsError } = useQuery({
    queryKey: ['logs', userId, logOffset],
    queryFn: async () => {
      const result = await logsAPI.get(userId, { limit: logLimit, offset: logOffset });
      if (result.success) return { logs: result.logs || [], total: result.total || 0 };
      throw new Error(result.error || '로그를 불러올 수 없습니다');
    },
    enabled: !!userId && tab === 'logs',
  });

  const logs = logsData?.logs || [];
  const logTotal = logsData?.total || 0;

  const logStats = logs.reduce((acc, log) => {
    if (log.status === 'success') acc.success++;
    else acc.failed++;
    return acc;
  }, { success: 0, failed: 0 });
  const successRate = logs.length > 0 ? ((logStats.success / logs.length) * 100).toFixed(1) : 0;

  // ─── 공통 헬퍼 ───
  const formatDateTime = (str) => {
    if (!str) return '';
    try {
      return new Date(str).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return str; }
  };

  const formatDateTimeFull = (str) => {
    if (!str) return '';
    try {
      return new Date(str).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return str; }
  };

  if (!user) return null;

  const isLoading = tab === 'reservations' ? loadingReservations : loadingLogs;
  const error = tab === 'reservations'
    ? (reservationsError?.message || null)
    : (logsError?.message || null);

  return (
    <Layout title="예약 내역">
      {/* 탭 전환 */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5">
        <button
          onClick={() => setTab('reservations')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            tab === 'reservations' ? 'bg-white text-charcoal shadow-sm' : 'text-sub-text'
          }`}
        >
          현재 예약
        </button>
        <button
          onClick={() => setTab('logs')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            tab === 'logs' ? 'bg-white text-charcoal shadow-sm' : 'text-sub-text'
          }`}
        >
          예약 로그
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 bg-coral/10 border border-coral/30 rounded-2xl p-4 animate-fade-up">
          <p className="text-sm font-medium text-coral">{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tayo" />
          <span className="ml-3 text-sub-text">불러오는 중...</span>
        </div>
      )}

      {/* ============ 현재 예약 탭 ============ */}
      {!isLoading && tab === 'reservations' && (
        <>
          {reservations.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-12 text-center animate-fade-up">
              <div className="w-16 h-16 bg-tayo-light rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-tayo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-charcoal mb-2">예약된 버스가 없어요</h3>
              <p className="text-sm text-sub-text mb-6">바로 예약하거나 자동 예약을 설정해보세요</p>
              <button onClick={() => navigate('/reserve')} className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl text-white bg-tayo hover:bg-tayo-dark btn-press shadow-card">
                바로 예약하기
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {reservations.map((r, i) => (
                <div key={`${r.busSeq}-${r.seatNo}-${i}`} className={`bg-white rounded-2xl shadow-card p-5 animate-fade-up stagger-${Math.min(i + 1, 8)}`}>
                  <div className="flex items-center space-x-3 mb-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                      r.direction === 'UP' ? 'bg-tayo-light text-tayo' : 'bg-orange-50 text-orange-600'
                    }`}>
                      {r.direction === 'UP' ? '등교' : '하교'}
                    </span>
                    <p className="text-lg font-semibold text-charcoal">{r.routeName}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center text-sub-text"><span className="mr-1.5">🚌</span>{r.carNum}호</div>
                    <div className="flex items-center text-sub-text"><span className="mr-1.5">🕒</span>{formatDateTime(r.operateTime)} 출발</div>
                    <div className="flex items-center text-sub-text"><span className="mr-1.5">📍</span>{r.stopName}</div>
                    <div className="flex items-center text-sub-text"><span className="mr-1.5">💺</span>{r.seatNo}번 좌석</div>
                  </div>
                  {r.reserveDate && <p className="mt-2 text-xs text-sub-text">예약일시: {formatDateTime(r.reserveDate)}</p>}
                  {r.canCancel && (
                    <button
                      onClick={() => handleCancel(r)}
                      disabled={cancellingReservation === `${r.busSeq}-${r.seatNo}`}
                      className="mt-3 w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-xl text-coral bg-coral/10 hover:bg-coral/20 btn-press disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancellingReservation === `${r.busSeq}-${r.seatNo}` ? '취소 중...' : '예약 취소'}
                    </button>
                  )}
                </div>
              ))}

              <div className="bg-tayo-light border border-tayo/20 rounded-2xl p-4 animate-fade-up">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-tayo flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-tayo-dark">예약 취소는 버스 출발 시간 전까지만 가능합니다.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ 예약 로그 탭 ============ */}
      {!isLoading && tab === 'logs' && (
        <>
          {/* 통계 */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="bg-tayo-light rounded-xl p-3 text-center">
              <p className="text-[10px] text-sub-text">전체</p>
              <p className="text-lg font-extrabold text-tayo">{logTotal}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-sub-text">성공</p>
              <p className="text-lg font-extrabold text-mint">{logStats.success}</p>
            </div>
            <div className="bg-coral/10 rounded-xl p-3 text-center">
              <p className="text-[10px] text-sub-text">실패</p>
              <p className="text-lg font-extrabold text-coral">{logStats.failed}</p>
            </div>
            <div className="bg-violet-50 rounded-xl p-3 text-center">
              <p className="text-[10px] text-sub-text">성공률</p>
              <p className="text-lg font-extrabold text-violet-600">{successRate}%</p>
            </div>
          </div>

          {logs.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-card p-12 text-center">
              <p className="text-sub-text">예약 기록이 없습니다</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="bg-white rounded-xl shadow-card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-sub-text">{formatDateTimeFull(log.executed_at)}</span>
                      <span className={`inline-flex text-xs font-semibold ${STATUS_BADGE[log.status] || 'bg-gray-100 text-gray-800 rounded-full px-2.5 py-0.5'}`}>
                        {STATUS_TEXT[log.status] || log.status}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-sub-text">노선</span>
                        <span className="text-sm font-medium text-charcoal">{log.route_name || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-sub-text">버스 시간</span>
                        <span className="text-sm font-medium text-charcoal">{log.bus_time || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-sub-text">좌석</span>
                        <span className="text-sm font-medium text-charcoal">{log.seat_no ? `${log.seat_no}번` : '-'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-sub-text">소요시간</span>
                        <span className="text-sm text-sub-text">
                          {log.metadata?.open_to_complete_ms ? `${log.metadata.open_to_complete_ms}ms`
                            : log.execution_time_ms ? `${log.execution_time_ms}ms` : '-'}
                        </span>
                      </div>
                      {log.error_message && (
                        <div className="pt-2 border-t border-border">
                          <span className="text-xs text-coral">{log.error_message}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* 페이지네이션 */}
              <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-sub-text">
                  <span className="font-medium text-charcoal">{logOffset + 1}</span>
                  {' - '}
                  <span className="font-medium text-charcoal">{Math.min(logOffset + logLimit, logTotal)}</span>
                  {' / '}
                  <span className="font-medium text-charcoal">{logTotal}</span>
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setLogOffset(Math.max(0, logOffset - logLimit))} disabled={logOffset === 0}
                    className="px-4 py-2 text-sm font-medium text-charcoal bg-gray-100 rounded-full hover:bg-gray-200 disabled:text-sub-text disabled:cursor-not-allowed transition-colors">
                    이전
                  </button>
                  <button onClick={() => setLogOffset(logOffset + logLimit)} disabled={logOffset + logLimit >= logTotal}
                    className="px-4 py-2 text-sm font-medium text-white bg-tayo rounded-full hover:bg-tayo-dark disabled:bg-gray-100 disabled:text-sub-text disabled:cursor-not-allowed transition-colors">
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
